import * as React from "react";
import {
    Button,
    FormControlLabel,
    FormGroup,
    Grid,
    Switch,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@material-ui/core";
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {Alert} from "@material-ui/lab";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import {
    activateProgramEnrollmentDefault,
    IActivateProgramEnrollmentRequest
} from "./request/ActivateProgramEnrollmentRequest";
import {Programs} from "../../../../consts/PacMan";

interface IProps {
    data?: any
}

interface IState {
    errorMessage: string
    isJsonInput: boolean
    isLoading: boolean
    jsonInput: string
    request: IActivateProgramEnrollmentRequest
    response: any
}

enum InputField {
    CONTRACT_ID,
    PROGRAM_NAME,
    VERSION
}

class ActivateProgramEnrollmentSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            errorMessage: '',
            isJsonInput: false,
            isLoading: false,
            jsonInput: activateProgramEnrollmentDefault,
            request: {} as IActivateProgramEnrollmentRequest,
            response: ''
        };

        this.toggleJsonInput = this.toggleJsonInput.bind(this);
        this.setRequest = this.setRequest.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public render() {
        return(
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                spacing={3}
            >
                <Alert variant="filled" severity="info">
                    Activate a program enrollment contract. Use the contractId and version from the Initiate Program Enrollment response.
                </Alert>

                <Grid item xs>
                    <FormGroup>
                        <FormControlLabel control={ <Switch onChange={ this.toggleJsonInput }/> } label="Toggle Json Input" />
                    </FormGroup>
                </Grid>

                { !this.state.isJsonInput &&
                    <Grid
                        container
                        direction="column"
                        justify="center"
                        alignItems="center"
                        spacing={3}
                    >
                        <Grid item xs>
                            <TextField
                                style={{width: 400}}
                                label="Contract Id (Required)"
                                variant="outlined"
                                value={ this.state.request.contractId || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.CONTRACT_ID, event) }
                            />
                        </Grid>
                        <Grid item xs>
                            <FormControl>
                                <InputLabel>Program Name (Required)</InputLabel>
                                <Select
                                    value={this.state.request.programName || ''}
                                    style={{width: 400}}
                                    onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.PROGRAM_NAME, event) }
                                >
                                    {Object.keys(Programs).map(program => (
                                        <MenuItem key={program} value={program}>
                                            <em>{program}</em>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs>
                            <TextField
                                style={{width: 400}}
                                label="Contract Version (Required)"
                                variant="outlined"
                                value={ this.state.request.version || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.VERSION, event) }
                            />
                        </Grid>
                    </Grid>
                }

                { this.state.isJsonInput &&
                    <Grid item xs>
                        <TextField
                            multiline
                            rows={ 8 }
                            label="Activate Program Enrollment Request"
                            variant="filled"
                            value={ this.state.jsonInput }
                            onChange={ this.setRequest }
                            style={{ width: 800 }}
                        />
                    </Grid>
                }

                <Grid item xs>
                    <Button variant="outlined" color="default" onClick={ this.handleSubmit }>
                        Submit
                    </Button>
                </Grid>

                { this.state.isLoading && <LoadingSpinner/> }

                { this.state.errorMessage &&
                    <Alert variant="filled" severity="error">
                        Error encountered: { this.state.errorMessage }
                    </Alert>
                }

                { this.state.response &&
                    <div>
                        <br/>
                        <h4>Status</h4>
                        <p style={{
                            border: '3px solid lightgray',
                            padding: '5px',
                            whiteSpace: "pre-wrap",
                            wordBreak: "normal",
                            wordWrap: "break-word"
                        }}>
                            <JsonRenderComponent jsonObject={ this.state.response } printHtml={ false }/>
                        </p>
                    </div>
                }
            </Grid>
        );
    }

    private toggleJsonInput() {
        this.setState({ isJsonInput: !this.state.isJsonInput });
    }

    private setRequestFields(field: InputField, event: React.ChangeEvent<HTMLTextAreaElement>) {
        const updatedRequest: IActivateProgramEnrollmentRequest = { ...this.state.request };

        switch (field) {
            case InputField.CONTRACT_ID: {
                updatedRequest.contractId = event.target.value;
                break;
            }
            case InputField.PROGRAM_NAME: {
                updatedRequest.programName = event.target.value;
                break;
            }
            case InputField.VERSION: {
                updatedRequest.version = event.target.value;
                break;
            }
            default: {
                break;
            }
        }
        this.setState({ request: updatedRequest });
    }

    private setRequest(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ jsonInput: event.target.value });
    }

    private handleSubmit() {
        this.setState({ isLoading: true, errorMessage: '', response: '' });
        let body: IActivateProgramEnrollmentRequest;

        if (this.state.isJsonInput) {
            try {
                body = JSON.parse(this.state.jsonInput);
            } catch (e) {
                this.setState({ isLoading: false, errorMessage: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` });
                return;
            }
        } else {
            body = this.state.request;
        }

        // Validate all fields and collect errors
        const errors: string[] = [];
        if (!body.contractId) errors.push('contract id');
        if (!body.programName) errors.push('program name');
        if (!body.version) errors.push('version');

        if (errors.length > 0) {
            this.setState({ isLoading: false, errorMessage: `Please provide: ${errors.join(', ')}` });
            return;
        }

        const config = {
            body,
            headers: {}
        };
        this.callActivateEnrollment(config);
    }

    private callActivateEnrollment(config: any) {
        const apiName = 'ApaqPortalAPI';
        const path = '/pharmacy-customer/activate-program-enrollment';

        console.log(config);
        API.post(apiName, path, config).then((enrollmentResponse: any) => {
            console.log(enrollmentResponse);
            this.setState({ response: enrollmentResponse, errorMessage: '', isLoading: false });
        }).catch((error: any) => {
            console.log(error);
            this.setState({
                response: '',
                errorMessage: error.response?.data?.message || error.message || 'An unknown error occurred',
                isLoading: false
            });
        });
    }
}

export default ActivateProgramEnrollmentSection;
